import { observer } from "mobx-react";
import React, { useEffect, useRef, useState } from "react";
import { Accordion } from "react-bootstrap";
import { AccordionSelectCallback } from "react-bootstrap/esm/AccordionContext";
import { useDrag, useDrop } from "react-dnd";
import toast from "react-hot-toast";
import { useStore } from "../../../../contexts/store-provider";
import {
  getDefaultActiveKeys,
  resetCategoriesTreatments
} from "../../../../helpers/category-helper";
import {
  CategoryDroppableProps,
  DragItem,
  ICategory,
  ItemTypes,
  SortableCategoryItemProps,
} from "../../../../models/state/IStoreTreatment";
import { formatMessage } from "../../../../translations/format-message";
import { SortableTreatmentRow } from "./sortable-treatment-row";

// Recursive component that handles category and subcategory rendering
const SortableCategoryItem: React.FC<SortableCategoryItemProps> = ({
  category,
  index,
  parentId,
  moveCategory,
  moveItem,
  onDetailClick,
  setCategories,
  storeId,
  openAccordions,
  setOpenAccordions,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const { treatmentManagementStore, categoryManagementStore } = useStore();
  const {
    GetTreatmentListByCatIdService,
    getTreatmentData,
    addUpdateStoreTreatmentOrderState,
    resetAddUpdateStoreTreatmentOrderState,
    getTreatmentStoreFilter,
    GetTreatmentListBySearchService,
  } = treatmentManagementStore;
  const { allCategoryForTreatmentStore } = categoryManagementStore;
  const [treatmentArray, setTreatmentArray] = useState<any[]>([]);
  const [accordionEventKey, setAccordionEventKey] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Fetch treatments when this category's accordion is opened
  useEffect(() => {
    if (!getTreatmentStoreFilter.IsFilterActive) {
      const eventKey = `${parentId || "root"}-${index}-${category.categoryId}`;
      if (openAccordions.includes(eventKey) && category.categoryId) {
        const fetchTreatmentsForCategory = async () => {
          setIsLoading(true);
          try {
            await GetTreatmentListByCatIdService(
              Number(category.categoryId),
              storeId
            );
            const treatments =
              getTreatmentData.get(Number(category.categoryId)) || [];
            setTreatmentArray(treatments);

            // Update the category with treatments in the parent state
            setCategories((prevCategories: ICategory[]) => {
              const updateCategoryWithTreatments = (
                categories: ICategory[]
              ): ICategory[] => {
                return categories.map((cat) => {
                  if (cat.categoryId === category.categoryId) {
                    return { ...cat, treatments };
                  }
                  if (cat.subCategories.length > 0) {
                    return {
                      ...cat,
                      subCategories: updateCategoryWithTreatments(
                        cat.subCategories
                      ),
                    };
                  }
                  return cat;
                });
              };
              return updateCategoryWithTreatments([...prevCategories]);
            });
          } catch (error) {
            console.error(
              `Error fetching treatments for category ${category.categoryId}:`,
              error
            );
          } finally {
            setIsLoading(false);
          }
        };

        fetchTreatmentsForCategory();
      }
    } else {
      try {
        // If there's a search query, use search service, otherwise get treatments for open accordions

        // Update the categories with correct treatment data
        setCategories((prevCategories: ICategory[]) => {
          // Updated type from any[] to ICategory[]
          // Helper function to process a category and its subcategories recursively

          // Process the search results and open relevant accordions
          // First, identify which categories have treatments
          const categoriesWithTreatments = new Set<number>();
          if (getTreatmentData && getTreatmentData.size > 0) {
            getTreatmentData.forEach((treatments, categoryId) => {
              if (treatments && treatments.length > 0) {
                categoriesWithTreatments.add(categoryId);
              }
            });
          } else {
            return resetCategoriesTreatments(prevCategories);
          }

          const categoryIndices = new Map<number, number>(); // categoryId -> index in categories array
          const parentChildMap = new Map<number, number[]>(); // parentId -> childIds

          const processCategoryHierarchy = (
            category: ICategory,
            index: number,
            parentId: number | null = null
          ) => {
            const categoryId = Number(category.categoryId);
            categoryIndices.set(categoryId, index);

            if (parentId !== null) {
              if (!parentChildMap.has(parentId)) {
                parentChildMap.set(parentId, []);
              }
              parentChildMap.get(parentId)!.push(categoryId);
            }

            if (category.subCategories && category.subCategories.length > 0) {
              category.subCategories.forEach((subCat, subIndex) => {
                processCategoryHierarchy(subCat, subIndex, categoryId);
              });
            }
          };

          // Process all categories to build the index and parent-child maps
          prevCategories.forEach((cat, index) =>
            processCategoryHierarchy(cat, index)
          );

          // Create set of prevCategories to open (including parents of prevCategories with treatments)
          const categoriesToOpen = new Set<number>();

          // Helper function to add a category and all its ancestors to the set
          const addCategoryWithAncestors = (categoryId: number) => {
            // First, add the category itself
            categoriesToOpen.add(categoryId);

            const parentEntries = Array.from(parentChildMap.keys());

            for (let i = 0; i < parentEntries.length; i++) {
              const parentId = parentEntries[i];
              const childIds = parentChildMap.get(parentId) || [];

              if (childIds.includes(categoryId)) {
                addCategoryWithAncestors(parentId);
                break;
              }
            }
          };

          // Add each category with treatments and its ancestors
          categoriesWithTreatments.forEach((categoryId) => {
            addCategoryWithAncestors(categoryId);
          });

          // Now create the set of accordion keys to open
          const newOpenAccordions = new Set<string>();

          // For each root category
          prevCategories.forEach((cat, index) => {
            const categoryId = Number(cat.categoryId);

            // If this category or any descendant has treatments, open it
            if (categoriesToOpen.has(categoryId)) {
              const eventKey = `root-${index}-${categoryId}`;
              newOpenAccordions.add(eventKey);
            }
          });

          // Update the open accordions
          // setOpenAccordions(newOpenAccordions);

          // Create deep copy to avoid direct state mutation
          const updatedCategories = JSON.parse(JSON.stringify(prevCategories));

          // Helper function to update treatments recursively
          const updateCategoryTreatments = (category: ICategory): ICategory => {
            const categoryId = Number(category.categoryId);

            // If this category has treatments in the data, update it
            if (categoriesWithTreatments.has(categoryId)) {
              category.treatments = getTreatmentData.get(categoryId) || [];
            }

            // Process subcategories if any
            if (category.subCategories && category.subCategories.length > 0) {
              category.subCategories = category.subCategories.map(
                updateCategoryTreatments
              );
            }

            return category;
          };
          // Update all categories
          const updatedCat = updatedCategories.map(updateCategoryTreatments);
          return updatedCat;
        });
      } catch (error) {
        console.error("Error refreshing treatments:", error);
      } finally {
        // setIsRefreshing(false);
      }
    }
  }, [
    category.categoryId,
    GetTreatmentListByCatIdService,
    getTreatmentData,
    index,
    openAccordions,
    parentId,
    setCategories,
    storeId,
    getTreatmentStoreFilter.IsFilterActive,
  ]);

  const currentEventKey = `${parentId || "root"}-${index}-${
    category.categoryId
  }`;
  const isAccordionOpen = openAccordions.includes(currentEventKey);

  // Handle treatment order update success
  useEffect(() => {
    if (addUpdateStoreTreatmentOrderState.success) {
      const fetchTreatmentsAfterReorder = async () => {
        if (!getTreatmentStoreFilter.IsFilterActive && isAccordionOpen) {
          try {
            await GetTreatmentListByCatIdService(
              Number(category.categoryId),
              storeId
            );
            const treatments =
              getTreatmentData.get(Number(category.categoryId)) || [];
            setTreatmentArray(treatments);

            // Update the main categories state
            setCategories((prevCategories: ICategory[]) => {
              const updateCategoryWithTreatments = (
                categories: ICategory[]
              ): ICategory[] => {
                return categories.map((cat) => {
                  if (cat.categoryId === category.categoryId) {
                    return { ...cat, treatments };
                  }
                  if (cat.subCategories.length > 0) {
                    return {
                      ...cat,
                      subCategories: updateCategoryWithTreatments(
                        cat.subCategories
                      ),
                    };
                  }
                  return cat;
                });
              };
              return updateCategoryWithTreatments([...prevCategories]);
            });
          } catch (error) {
            console.error("Error fetching treatments after reorder:", error);
          }
        } else if (getTreatmentStoreFilter.IsFilterActive) {
          try {
            // Clear previous search results immediately
            setCategories((prev: ICategory[]) =>
              resetCategoriesTreatments(prev)
            );

            await GetTreatmentListBySearchService(
              getTreatmentStoreFilter.Search
            );

            // Force UI update with new treatment data
            setCategories((prev: ICategory[]) => {
              if (getTreatmentData.size === 0) {
                // toast.success("no_data_found");
                setOpenAccordions([]);
                return [];
              }

              const updateCategories = (cats: ICategory[]): ICategory[] =>
                cats.map((cat) => ({
                  ...cat,
                  treatments:
                    getTreatmentData.get(Number(cat.categoryId)) || [],
                  subCategories: updateCategories(cat.subCategories),
                }));

              const finalUpdatedCategories = updateCategories(
                allCategoryForTreatmentStore
              );
              setOpenAccordions(getDefaultActiveKeys(finalUpdatedCategories));
              return finalUpdatedCategories;
            });
          } catch (error) {
            toast.error(formatMessage("search_failed"));
          }
        }
      };

      fetchTreatmentsAfterReorder();
      resetAddUpdateStoreTreatmentOrderState();
    }
  }, [
    addUpdateStoreTreatmentOrderState.success,
    accordionEventKey,
    category.categoryId,
    GetTreatmentListByCatIdService,
    getTreatmentData,
    resetAddUpdateStoreTreatmentOrderState,
    setCategories,
    storeId,
    openAccordions.length,
    getTreatmentStoreFilter.IsFilterActive,
    openAccordions,
    parentId,
    index,
    getTreatmentStoreFilter.Search,
    isAccordionOpen,
    GetTreatmentListBySearchService,
    allCategoryForTreatmentStore,
    setOpenAccordions,
  ]);

  // Update treatments when getTreatmentData changes
  useEffect(() => {
    if (accordionEventKey && getTreatmentData) {
      const treatments =
        getTreatmentData.get(Number(category.categoryId)) || [];
      setTreatmentArray(treatments);
    }
  }, [getTreatmentData, category.categoryId, accordionEventKey]);

  // Set up drag
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.CATEGORY,
    item: () => ({
      type: ItemTypes.CATEGORY,
      id: category.categoryId,
      index,
      parentId,
    }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  interface DropResult {
    handlerId: string | symbol | null;
  }

  // Set up drop - ONLY handle drop event, not hover
  const [{ handlerId }, drop] = useDrop<DragItem, void, DropResult>(
    {
      accept: ItemTypes.CATEGORY,
      collect(monitor) {
        return {
          handlerId: monitor.getHandlerId(),
        };
      },
      // Use canDrop to show visual feedback
      canDrop(item: DragItem, monitor) {
        const dragParentId = item.parentId;
        const hoverParentId = parentId;
        // Only allow dropping within the same parent level
        return dragParentId === hoverParentId;
      },
      // Only execute on drop, not on hover
      drop(item: DragItem, monitor) {
        if (!ref.current) {
          return;
        }
        const dragParentId = item.parentId;
        const dragCategoryId = Number(item.id);
        const hoverCategoryId = Number(category.categoryId);
        const hoverParentId = parentId;
        
        // Only allow reordering within the same parent level
        if (dragParentId !== hoverParentId) {
          return;
        }

        const dragIndex = item.index;
        const hoverIndex = index;

        // Don't replace items with themselves
        if (dragIndex === hoverIndex) {
          return;
        }

        // Time to actually perform the action - THIS WILL TRIGGER API CALL
        moveCategory(
          parentId,
          dragIndex,
          hoverIndex,
          dragCategoryId,
          hoverCategoryId
        );
      },
    },
    [moveCategory, parentId, index, category.categoryId]
  );

  drag(drop(ref));

  // Track accordion open/close state
  const handleAccordionClick: AccordionSelectCallback = (e) => {
    setOpenAccordions(
      Array.isArray(e)
        ? e.filter((key): key is string => key !== null && key !== undefined)
        : [e].filter((key): key is string => key !== null && key !== undefined)
    );
  };

  return (
    <Accordion
      className="mt-2"
      alwaysOpen
      defaultActiveKey={openAccordions}
      activeKey={openAccordions}
      onSelect={handleAccordionClick}
    >
      <div className="categories-container">
        <div ref={ref} data-handler-id={handlerId}>
          <Accordion.Item
            key={category.categoryId}
            eventKey={currentEventKey}
            aria-activedescendant={`${currentEventKey}`}
          >
            <Accordion.Header
            >
              <div className="d-flex align-items-center w-100">
                <span className="me-3">
                  <i className="fas fa-grip-vertical drag-handle">
                    <svg
                      id="fi_8379631"
                      height="12"
                      viewBox="0 0 24 24"
                      width="12"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        id="drag-vertical"
                        d="m10 19a2 2 0 1 1 -2-2 2 2 0 0 1 2 2zm-2-9a2 2 0 1 0 2 2 2 2 0 0 0 -2-2zm0-7a2 2 0 1 0 2 2 2 2 0 0 0 -2-2zm8 14a2 2 0 1 0 2 2 2 2 0 0 0 -2-2zm0-7a2 2 0 1 0 2 2 2 2 0 0 0 -2-2zm0-7a2 2 0 1 0 2 2 2 2 0 0 0 -2-2z"
                        fill="rgb(0,0,0)"
                      ></path>
                    </svg>
                  </i>
                </span>
                <span className="flex-grow-1 text-wrap">{category.name}</span>
                {isAccordionOpen && isLoading && (
                  <span
                    className="ms-2 spinner-border spinner-border-sm text-primary"
                    role="status"
                    aria-hidden="true"
                  ></span>
                )}
              </div>
            </Accordion.Header>
            <Accordion.Body>
              {/* Render treatments if available */}
              {category.treatments && category.treatments.length > 0 ? (
                <div className="table-responsive">
                  <table className="table border">
                    <thead>
                      <tr>
                        <th></th>
                        <th className="col-sr-no">{formatMessage("sr_no")}</th>
                        <th className="col-name">{formatMessage("name")}</th>
                        <th className="col-fee">{formatMessage("fee")}</th>
                        <th className="col-details">
                          {formatMessage("details")}
                        </th>
                      </tr>
                    </thead>
                    <CategoryDroppable id={category.categoryId}>
                      {category.treatments.map((treatment, index) => (
                        <SortableTreatmentRow
                          key={`treatment-${treatment.Id}-${category.categoryId}`}
                          id={`treatment-${treatment.Id}-${category.categoryId}`}
                          treatment={treatment}
                          index={index}
                          categoryId={category.categoryId}
                          moveItem={moveItem}
                          onDetailClick={onDetailClick}
                        />
                      ))}
                    </CategoryDroppable>
                  </table>
                </div>
              ) : (category.treatments && category.treatments.length === 0 && category?.subCategories?.length === 0) ? (
                <div>
                  {formatMessage("no_treatments_found_in_this_category")}
                </div>
              ) : null}

              {/* Recursively render subcategories */}
              {category.subCategories.length > 0 && (
                <>
                  {category.subCategories.map((subCategory, subIndex) => (
                    <SortableCategoryItem
                      key={subCategory.categoryId}
                      category={subCategory}
                      index={subIndex}
                      parentId={category.categoryId}
                      moveCategory={moveCategory}
                      moveItem={moveItem}
                      onDetailClick={onDetailClick}
                      setCategories={setCategories}
                      storeId={storeId}
                      openAccordions={openAccordions}
                      setOpenAccordions={setOpenAccordions}
                    />
                  ))}
                </>
              )}
            </Accordion.Body>
          </Accordion.Item>
        </div>
      </div>
    </Accordion>
  );
};

export default observer(SortableCategoryItem);

// Droppable category component for treatments
export const CategoryDroppable: React.FC<CategoryDroppableProps> = observer(
  ({ id, children }) => {
    return <tbody>{children}</tbody>;
  }
);
